import { registerFeatureBootstrap } from "../bootstrap";
import { closestFromEvent } from "../utils/dom";
import {
  defaultHubRuntimeEnv,
  postHubForm,
  readHubErrorMessage,
  setHubFeedback,
  type HubRuntimeEnv,
} from "./hub_actions";

const SKILLS_HUB_SELECTOR = "[data-skills-hub-root]";

const cleanupByRoot = new WeakMap<HTMLElement, () => void>();

function nodeWithinRoot(root: HTMLElement, node: HTMLElement | null): HTMLElement | null {
  return node instanceof HTMLElement && root.contains(node) ? node : null;
}

function feedbackNode(root: HTMLElement): HTMLElement | null {
  const node = root.querySelector("[data-skills-feedback]");
  return node instanceof HTMLElement ? node : null;
}

function showSkillsFeedback(root: HTMLElement, message: string, isError: boolean): void {
  setHubFeedback(feedbackNode(root), message, isError);
}

function skillsCards(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll("[data-skills-card]")).filter((node): node is HTMLElement => node instanceof HTMLElement);
}

function skillsActiveIndex(root: HTMLElement): number {
  const raw = Number(root.dataset.skillsActiveCard || "0");
  if (!Number.isFinite(raw) || raw < 0) {
    return 0;
  }
  return Math.floor(raw);
}

function setSkillsActiveIndex(root: HTMLElement, index: number): void {
  root.dataset.skillsActiveCard = String(index);
}

export function syncSkillsCarousel(root: HTMLElement): void {
  const cards = skillsCards(root);
  if (cards.length === 0) {
    setSkillsActiveIndex(root, 0);
    return;
  }
  const nextIndex = Math.min(cards.length - 1, Math.max(0, skillsActiveIndex(root)));
  setSkillsActiveIndex(root, nextIndex);
  cards.forEach((card, index) => {
    card.classList.toggle("is-active", index === nextIndex);
  });
}

export function advanceSkillsCarousel(root: HTMLElement): void {
  const cards = skillsCards(root);
  if (cards.length === 0) {
    return;
  }
  setSkillsActiveIndex(root, Math.min(cards.length - 1, skillsActiveIndex(root) + 1));
  syncSkillsCarousel(root);
}

function declarationQRCodeID(root: HTMLElement): string {
  const field = root.querySelector("[data-skills-declare] [name='qr_code_id']");
  return field instanceof HTMLInputElement ? field.value.trim() : "";
}

async function submitSkillsRequest(
  root: HTMLElement,
  url: string,
  body: FormData | URLSearchParams,
  successMessage: string,
  failureMessage: string,
  env: HubRuntimeEnv,
): Promise<void> {
  const result = await postHubForm(url, body);
  if (!result.ok) {
    showSkillsFeedback(root, readHubErrorMessage(result.error, failureMessage), true);
    return;
  }
  if (successMessage) {
    showSkillsFeedback(root, successMessage, false);
  }
  env.location.reload();
}

function bindSkillsClicks(root: HTMLElement, env: HubRuntimeEnv, signal: AbortSignal): void {
  root.addEventListener("click", (event) => {
    const passButton = nodeWithinRoot(root, closestFromEvent(event, "[data-skills-carousel-pass]"));
    if (passButton) {
      advanceSkillsCarousel(root);
      return;
    }

    const carouselConnectButton = nodeWithinRoot(root, closestFromEvent(event, "[data-skills-carousel-connect]"));
    if (carouselConnectButton) {
      const card = skillsCards(root)[skillsActiveIndex(root)];
      const connectButton = card?.querySelector("[data-skills-connect]");
      if (connectButton instanceof HTMLButtonElement) {
        connectButton.click();
      }
      return;
    }

    const connectButton = nodeWithinRoot(root, closestFromEvent(event, "[data-skills-connect]"));
    if (connectButton instanceof HTMLButtonElement) {
      const targetID = String(connectButton.getAttribute("data-skills-connect-id") || "").trim();
      const url = String(connectButton.getAttribute("data-skills-connect") || "").trim();
      const qrCodeID = declarationQRCodeID(root);
      if (!targetID || !url || !qrCodeID) {
        return;
      }
      const body = new URLSearchParams();
      body.set("target_user_id", targetID);
      body.set("qr_code_id", qrCodeID);
      void submitSkillsRequest(root, url, body, "Connection request sent.", "Failed to connect.", env);
      return;
    }

    const matchButton = nodeWithinRoot(root, closestFromEvent(event, "[data-skills-match-url]"));
    if (matchButton instanceof HTMLButtonElement) {
      const url = String(matchButton.getAttribute("data-skills-match-url") || "").trim();
      if (!url) {
        return;
      }
      void submitSkillsRequest(root, url, new URLSearchParams(), "", "Failed to update match.", env);
    }
  }, { signal });
}

function bindSkillsSubmits(root: HTMLElement, env: HubRuntimeEnv, signal: AbortSignal): void {
  root.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !root.contains(form) || !form.matches("[data-skills-declare]")) {
      return;
    }
    event.preventDefault();
    const url = String(form.getAttribute("data-skills-declare") || "").trim();
    if (!url) {
      return;
    }
    void submitSkillsRequest(root, url, new FormData(form), "Declaration saved.", "Failed to save declaration.", env);
  }, { signal });
}

export function mountSkillsHub(root: HTMLElement, env: HubRuntimeEnv = defaultHubRuntimeEnv()): void {
  const ctrl = new AbortController();
  syncSkillsCarousel(root);
  bindSkillsClicks(root, env, ctrl.signal);
  bindSkillsSubmits(root, env, ctrl.signal);
  cleanupByRoot.set(root, () => {
    ctrl.abort();
  });
}

export function unmountSkillsHub(root: HTMLElement): void {
  cleanupByRoot.get(root)?.();
  cleanupByRoot.delete(root);
}

registerFeatureBootstrap({
  key: "participant:skills-hub",
  selector: SKILLS_HUB_SELECTOR,
  mount(root) {
    mountSkillsHub(root);
  },
  unmount(root) {
    unmountSkillsHub(root);
  },
});
